import { createSelector } from '@reduxjs/toolkit';

import { createTagUrl } from '~/atomic/atom/links/createTagUrl';
import {
  catalogFilterFirstGenreSelector,
  catalogFilterIsFreeBooksSelector,
  catalogFilterIsHaveGenreSelector,
  catalogFilterIsHaveTagSelector, catalogFilterSelector,
} from '~/feature/catalog/filter/catalogFilter.selector';
import { paginationTotalSelector } from '~/feature/pagination/pagination.selector';
import { currentTagSelector, tagSelector } from '~/feature/tag/tag.selector';
import { RootState } from '~/store';

export const tagFiltersSelector = (state: RootState) => state.pagesFilter.tag;

export const metaTitleSelector = createSelector(
  catalogFilterIsHaveTagSelector,
  catalogFilterIsHaveGenreSelector,
  catalogFilterIsFreeBooksSelector,
  catalogFilterFirstGenreSelector,
  currentTagSelector,
  (isHaveTag, isHaveGenre, isFreeBooks, firstGenre, tag) => {
    if (!isHaveTag && !isFreeBooks) {
      return 'Читать и скачивать в fb2 книги полностью';
    }

    if (isHaveTag && !isFreeBooks) {
      return `Книги про ${
        tag.nameVin
      } – читать книги онлайн и скачивать в fb2`;
    }

    if (isHaveTag && isFreeBooks) {
      return `Книги про ${tag.nameVin} – бесплатно читать книги онлайн и скачивать в fb2 `;
    }

    return 'Читать и скачивать в fb2 книги полностью';
  },
);

export const metaDescriptionSelector = createSelector(
  catalogFilterIsHaveTagSelector,
  catalogFilterIsHaveGenreSelector,
  catalogFilterIsFreeBooksSelector,
  (isHaveTag, isHaveGenre, isFreeBooks) => {
    if (!isHaveTag && !isFreeBooks) {
      return 'Библиотека книг с рекомендательной системой. Возможность общения с авторами. '
        + 'Книги полностью.';
    }

    if (isHaveTag && !isFreeBooks) {
      return `Библиотека книг с рекомендательной системой. 
      Возможность общения с авторами. Книги полностью.`;
    }

    if (isHaveTag && isFreeBooks) {
      return `Прекрасная библиотека книг с рекомендательной системой. 
      Возможность общения с авторами. Книги полностью.`;
    }
  },
);

export const metaCanonicalSelector = createSelector(
  catalogFilterSelector,
  ({
    isWithAbonnement,
    isCanDownload,
    tagSlug,
    isOnlyFree,
    mainGenreSlug,
    bookType,
    completeStatus,
    withAudiobook,
    sortingType,
  }) => {
    return createTagUrl({
      tagSlug,
      genreSlug: mainGenreSlug,
      isOnlyFree,
      isCanDownload,
      isWithAbonnement,
      bookType,
      withAudiobook,
      completeStatus,
      sortingType,
    });
  },
);

export const ogTitleSelector = createSelector(
  catalogFilterIsHaveTagSelector,
  catalogFilterIsHaveGenreSelector,
  catalogFilterIsFreeBooksSelector,
  catalogFilterFirstGenreSelector,
  currentTagSelector,
  (isHaveTag, isHaveGenre, isFreeBooks, firstGenre, tag) => {
    if (!isHaveTag && !isFreeBooks) {
      return 'Читать и скачивать в fb2 книги полностью';
    }

    if (isHaveTag && !isFreeBooks) {
      return `Книги про ${tag.nameVin} читать онлайн | Произведения авторов на Bookriver`;
    }

    return 'Читать и скачивать в fb2 книги полностью';
  },
);

export const h1ContentSelector = createSelector(
  catalogFilterIsHaveTagSelector,
  catalogFilterIsHaveGenreSelector,
  catalogFilterIsFreeBooksSelector,
  catalogFilterFirstGenreSelector,
  currentTagSelector,
  (isHaveTag, isHaveGenre, isFreeBooks, firstGenre, tag) => {
    if (!isHaveTag && !isFreeBooks) {
      return 'Все книги';
    }

    if (!isHaveTag && isFreeBooks) {
      return 'Все бесплатные книги';
    }

    if (isHaveTag && !isFreeBooks) {
      return tag.name;
    }

    if (isHaveTag && isFreeBooks) {
      return tag.name;
    }

    return '';
  },
);

export const ogDescriptionSelector = createSelector(
  catalogFilterIsHaveTagSelector,
  catalogFilterIsHaveGenreSelector,
  catalogFilterIsFreeBooksSelector,
  catalogFilterFirstGenreSelector,
  currentTagSelector,
  (isHaveTag, isHaveGenre, isFreeBooks, firstGenre, tag) => {
    if (!isHaveTag && !isFreeBooks) {
      return 'Библиотека книг с рекомендательной системой. Возможность общения с авторами. '
        + 'Книги полностью.';
    }

    if (isHaveTag && !isFreeBooks) {
      return `Книги про ${tag.nameVin}`
        + ' читать на литературно-издательской площадке Bookriver ⭐ Серии авторских произведений'
        + ' ⭐ Подборка романов и рассказов авторов ⭐ Скачать произведения полностью '
        + ' в формате fb2 ✉ Найдите книги интересующей вас тематики на нашем сайте'
        + ' и приобретайте в свою библиотеку';
    }

    if (isHaveTag && isFreeBooks) {
      return `Книги про ${tag.nameVin}`
        + ' читать на литературно-издательской площадке Bookriver ⭐ Серии авторских произведений'
        + ' ⭐ Подборка романов и рассказов авторов ⭐ Скачать произведения полностью '
        + ' в формате fb2 ✉ Найдите книги интересующей вас тематики на нашем сайте'
        + ' и приобретайте в свою библиотеку';
    }

    return 'Библиотека книг с рекомендательной системой. Возможность общения с авторами. '
      + 'Книги полностью.';
  },
);

export const isHidePageSelector = createSelector(
  catalogFilterIsHaveTagSelector,
  catalogFilterIsHaveGenreSelector,
  catalogFilterIsFreeBooksSelector,
  paginationTotalSelector,
  (isHaveTag, isHaveGenre, isFreeBooks, booksTotal) => {
    if (!isHaveTag && !isFreeBooks && booksTotal === 0) {
      return true;
    }

    if (!isHaveTag && isFreeBooks && booksTotal < 1) {
      return true;
    }

    if (isHaveTag && !isFreeBooks && booksTotal < 1) {
      return true;
    }

    if (isHaveTag && isFreeBooks && booksTotal < 1) {
      return true;
    }

    return false;
  },
);

export const tagPageSeoSelector = createSelector(
  metaTitleSelector,
  metaDescriptionSelector,
  metaCanonicalSelector,
  h1ContentSelector,
  ogTitleSelector,
  ogDescriptionSelector,
  isHidePageSelector,
  tagSelector,
  (
    metaTitle,
    metaDescription,
    metaCanonical,
    h1Content,
    ogTitle,
    ogDescription,
    isHidePage,
    { currentTagSeo },
  ) => {
    return ({
      meta: {
        title: (!currentTagSeo?.title || currentTagSeo?.title === '')
          ? metaTitle
          : currentTagSeo.title,
        description: (!currentTagSeo?.description || currentTagSeo?.description === '')
          ? metaDescription
          : currentTagSeo?.description,
        canonical: metaCanonical,
      },
      og: {
        title: (!currentTagSeo?.title || currentTagSeo?.title === '')
          ? ogTitle
          : currentTagSeo?.title,
        description: (!currentTagSeo?.description || currentTagSeo?.description === '')
          ? ogDescription
          : currentTagSeo?.description,
      },
      h1: (!currentTagSeo?.h1 || currentTagSeo?.h1 === '')
        ? h1Content
        : currentTagSeo?.h1,
      isHidePage,
    });
  },
);
